/* The presentation model for alert routing. Pure functions, no React, no fetch.
 *
 * **Two things on this screen are statements about a customer's alerting, so they are derived here
 * and tested.**
 *
 * `declared` is the first. `false` means the policy is the *platform's* default — critical incidents,
 * by email, to the destination the operator configured — and nobody has chosen. The API sends that
 * default as a rule with the nil id; it is not a rule this tenant declared, and the screen does not
 * list it as one.
 *
 * Per-kind coverage is the second and the more useful one. A policy can be declared, well-formed,
 * stored, and *not route a critical incident anywhere* — a tenant who declared "info volume anomalies
 * to a webhook" and nothing else has silently stopped being paged about a dropped column. The API
 * cannot say that (a rule is valid in isolation) and the screen must, because the failure is silence.
 */

import type {
  IncidentKind,
  NotificationRule,
  RoutingPolicy,
  Severity,
} from '@/shared/api/generated/observability';

/** Severity as an order, mirroring the backend's `_SEVERITY_RANK`.
 *
 * Declared rather than derived from the string: alphabetically 'critical' < 'info' < 'warning', so a
 * text comparison would be wrong for most pairs. Total, so a fourth member is a compile error here
 * rather than a floor that silently admits it.
 */
const SEVERITY_RANK = {
  info: 0,
  warning: 1,
  critical: 2,
} satisfies Record<Severity, number>;

/** Whether a rule would send an incident of this severity. Kind is a separate question. */
export function admitsSeverity(rule: NotificationRule, severity: Severity): boolean {
  if (!rule.enabled) return false;
  return SEVERITY_RANK[severity] >= SEVERITY_RANK[rule.min_severity];
}

/** Whether a rule would send an incident of this kind. `null` on the rule means every kind. */
export function admitsKind(rule: NotificationRule, kind: IncidentKind): boolean {
  return rule.incident_kind === null || rule.incident_kind === kind;
}

/** The nil UUID the API gives the platform's default rule, which is not a stored row. */
export const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** Whether a rule is the platform's default rather than one the tenant declared. */
export function isDefaultRule(rule: NotificationRule): boolean {
  return rule.id === NIL_UUID;
}

/** The rules this tenant declared. The platform default, when it is all there is, is not one. */
export function declaredRules(policy: RoutingPolicy): readonly NotificationRule[] {
  return policy.declared ? policy.rules.filter((rule) => !isDefaultRule(rule)) : [];
}

/** The declared, enabled rules that would send an incident of this kind and severity. */
export function reaching(
  policy: RoutingPolicy,
  kind: IncidentKind,
  severity: Severity,
): readonly NotificationRule[] {
  return declaredRules(policy).filter(
    (rule) => admitsSeverity(rule, severity) && admitsKind(rule, kind),
  );
}

/** One kind's critical coverage, as the coverage card draws it.
 *
 * Three states rather than a boolean: `default` when nothing is declared (the platform's default
 * routes criticals, which is not this tenant's decision), `covered` with how many rules reach it, and
 * `nobody` — the one that must be loud, because it is a critical incident of this kind that would be
 * recorded and told to no one.
 */
export interface KindCoverage {
  readonly kind: IncidentKind;
  readonly state: 'default' | 'covered' | 'nobody';
  readonly rules: number;
}

export function kindCoverage(
  policy: RoutingPolicy,
  kinds: readonly IncidentKind[],
): readonly KindCoverage[] {
  return kinds.map((kind) => {
    const rules = reaching(policy, kind, 'critical').length;
    return {
      kind,
      rules,
      state: !policy.declared ? 'default' : rules > 0 ? 'covered' : 'nobody',
    };
  });
}

/** Where a rule sends, as one line. */
export function destinationOf(rule: NotificationRule): string {
  if (rule.target_kind === 'email_recipients') return rule.recipients.join(', ');
  if (rule.target_kind === 'webhook_url' && rule.webhook_url !== null)
    return rule.webhook_url;
  return rule.destination;
}

function emailDomainOf(address: string): string | null {
  const trimmed = address.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0) return null;
  const domain = trimmed.slice(at + 1).toLowerCase();
  return domain === '' || !domain.includes('.') ? null : domain;
}

/** The recipients not on the tenant's mail domain.
 *
 * Whole-domain equality, case-insensitive: `ops@bank-a.example` is on `bank-a.example`, and
 * `ops@evil-bank-a.example` and `ops@mail.bank-a.example` are not — the server matches whole labels,
 * and a subdomain is a different domain to it. Checking here only means a reader is told before they
 * submit; `NotificationRoutingService` is the enforcement, reading the domain from the tenant record.
 */
export function addressesOutside(
  allowedDomain: string,
  recipients: readonly string[],
): readonly string[] {
  const wanted = allowedDomain.toLowerCase();
  return recipients.filter((address) => emailDomainOf(address) !== wanted);
}
